import { useMutation, useQuery } from '@tanstack/react-query';
import apiClient from '../config/api-client';
import { ApiProject } from '../constants/ApiProject';
import { ProjectResponse, ProjectDetailResponse, DeleteProjectRequest, CollaborationProjectReqResponse, ApproveReqRequest, RejectReqRequest, SendCollaborationReqRequest } from '../types/project';
import { UseMutationOptions, UseQueryOptions } from '../types/react-query';
import { SendHandle } from 'child_process';

// ========================================
// QUERIES
// ========================================
export const useQueryProject = ({
  query,
  options,
}: {
  query: { userId: string, search?: string, take?: number, page?: number, startDate?: string, endDate?: string };
  options?: UseQueryOptions<ProjectResponse>;
}) =>
  useQuery(
    ['project', query.take, query.page],
    () =>
      apiClient
        .get<unknown, { data: ProjectResponse }>(ApiProject.projectList(query.userId, query.search, query.take, query.page, query.startDate, query.endDate))
        .then((res) => res.data),
    options,
  );

export const useQueryProjectDetail = ({
  query,
  options,
}: {
  query: { userId: string, projectId: string, take?: number, page?: number};
  options?: UseQueryOptions<ProjectDetailResponse>;
}) =>
  useQuery(
    ['projectDetail', query],
    () =>
      apiClient
        .get<ProjectDetailResponse>(ApiProject.projectDetail(query.userId, query.projectId, query.take, query.page))
        .then((res) => res.data),
    {
      // enabled: true,
      ...options,
    },
  );
  
export const useMutationCreateProject = (options?: UseMutationOptions) =>
  useMutation(
    (data: FormData) =>
      apiClient
        .post<unknown, { data: string }>(`${ApiProject.createProject}`, data)
        .then((res) => res.data),
    options,
  );

export const useMutationDeleteProject = (options?: UseMutationOptions) =>
  useMutation(
    (data: DeleteProjectRequest) =>
      apiClient
        .post<unknown, { data: string }>(`${ApiProject.deleteProject}`, data)
        .then((res) => res.data),
    options,
  );

export const useMutationUpdateProject = (options?: UseMutationOptions) =>
  useMutation(
    (data: FormData) =>
      apiClient
        .post<unknown, { data: string }>(`${ApiProject.updateProject}`, data)
        .then((res) => res.data),
    options,
  );

export const useQueryCollaborationProjectReq = ({
    query,
    options,
  }: {
    query: { userId: string, search: string, take: number, page: number, startDate: string, endDate: string };
    options?: UseQueryOptions<CollaborationProjectReqResponse>;
  }) =>
    useQuery(
      ['collaborationReq', query.take, query.page],
      () =>
        apiClient
          .get<unknown, { data: CollaborationProjectReqResponse }>(ApiProject.collaborationProjectReqList(query.userId, query.search, query.take, query.page, query.startDate, query.endDate))
          .then((res) => res.data),
      options,
    );

export const useMutationApproveReq = (options?: UseMutationOptions) =>
  useMutation(
    (data: ApproveReqRequest) =>
      apiClient
        .post<unknown, { data: string }>(`${ApiProject.approveCollaborationReq}`, data)
        .then((res) => res.data),
    options,
  );

export const useMutationRejectReq = (options?: UseMutationOptions) =>
  useMutation(
    (data: RejectReqRequest) =>
      apiClient
        .post<unknown, { data: string }>(`${ApiProject.rejectCollaborationReq}`, data)
        .then((res) => res.data),
    options,
  );

export const useMutationSendCollaborationReq = (options?: UseMutationOptions) =>
  useMutation(
    (data: SendCollaborationReqRequest) =>
      apiClient
        .post<unknown, { data: string }>(`${ApiProject.reqToCollaborate}`, data)
        .then((res) => res.data),
    options,
  );

export const useQueryDownloadCSV = ({
    query,
    options,
  }: {
    query: { projectId: string };
    options?: UseQueryOptions<any>;
  }) =>
    useQuery(
      ['downloadCSV'],
      () =>
        apiClient
          .get<any>(ApiProject.downloadCSV(query.projectId), {
            responseType: 'blob'
          })
          .then((res) => res.data),
      options,
    );